import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ModalService } from '@purespectrum1/ui/modal';
import { TableExpandableRowDirective } from '@purespectrum1/ui/table';
import { ToasterService } from '@purespectrum1/ui/toaster-service';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { filter, map, shareReplay, switchMap, tap } from 'rxjs/operators';

import { AuthService } from '../../../shared/services/auth/auth.service';
import { ConfirmationModalComponent } from '../../../shared/ui/confirmation-modal/confirmation-modal.component';
import { Constants } from '../../../shared/constants/create-survey.constants';
import { Constants as QuotaConstants } from '../../constants/quotas';
import {
  NestedQuota,
  QuotaPayload,
} from '../../../shared/interfaces/quota.interface';
import { NestingQualification } from '../../../shared/interfaces/quota-nesting.interface';
import { notifyMessage } from '../../../shared/constants/notify-message';
import { NestedQuotasResetEvaluator } from '../domain/nested-quotas-reset-evaluator';
import { SurveyNestedQuotas } from '../../domain/survey-nested-quotas';

import {
  EachNestingGroupData,
  isNestedQuota,
} from '../interfaces/multi-quota-nesting.interface';
import { MultiQuotaNestingService } from '../services/multi-quota-nesting.service';
import { NestedQuotaGroupBuilder } from '../domain/nested-quota-group-builder';
import { NestedQuotaGroupSeparator } from '../domain/nested-quota-group-separator';
import { IndividualQuotaCountDistributor } from '../domain/individual-quota-count-distributor';
import { NestedQuotaResetter } from '../domain/nested-quota-resetter';
import { IndividualQuotaAllocation } from '../../quota-nesting/domain/individual-quota-allocation';
import { NestedQuotaPercentageMechanic } from '../../domain/nested-quota-percentage-mechanic';
import { BuyerConfigSettingsVerifier } from '../../../shared/utils/buyer-config-settings-verifier';
import { SurveyUnicorns } from '../../domain/survey-unicorns';
import { QuotaPayloadService } from '../../services/quotas/quota-payload.service';

interface NestedQuotaRecord extends NestedQuota, Record<string, unknown> {}

type QuotaEditKey = keyof Pick<
  NestedQuota,
  'isQuotaTitleEdit' | 'isPercentageEdit' | 'isAllocationEdit'
>;

@Component({
  selector: 'ps-ui-multi-quota-nesting-table',
  templateUrl: './multi-quota-nesting-table.component.html',
  styleUrls: ['./multi-quota-nesting-table.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MultiQuotaNestingTableComponent implements OnInit {
  @ViewChild(TableExpandableRowDirective)
  public expandable!: TableExpandableRowDirective;

  @Input()
  public originalQuotas: QuotaPayload[] = [];

  @Input()
  public surveyAllocation: number = 0;

  @Input()
  public buyerCompanyId: number;

  public isCombineQuotasEnabled: boolean = false;
  public nestedQuotaRecords$!: Observable<NestedQuotaRecord[]>;
  public nestedQuotaGroupIds$!: Observable<string[]>;
  public selectedQualifications$!: Observable<NestingQualification[]>;
  public selectedNestingGroup$!: Observable<EachNestingGroupData>;

  public remainingAllocation: number = 0;
  public surveyUnicorns: SurveyUnicorns = new SurveyUnicorns([]);
  readonly QuotaConstants = QuotaConstants;
  public hasUnicornQuota: boolean = false;

  private _selectedNestingGroup: EachNestingGroupData = <
    EachNestingGroupData
  >{};

  /** Selected Quota IDs */
  private _selecteds: string[] = [];

  private _deleteSelectedQuotasCommand$: BehaviorSubject<{
    ids: string[];
    type: 'single' | 'multiple';
  }> = new BehaviorSubject<{ ids: string[]; type: 'single' | 'multiple' }>({
    ids: [] as string[], // explicitly a string array
    type: 'multiple',
  });

  constructor(
    private _multiQuotaNestingService: MultiQuotaNestingService,
    private _toastr: ToasterService,
    private _modal: ModalService,
    private _authService: AuthService,
    private _quotaPayloadService: QuotaPayloadService
  ) {}

  ngOnInit(): void {
    this.isCombineQuotasEnabled = this._isCombineQuotasEnabled();
    this._handleNestedQuotasStreams();
    this._handleMultipleNestedQuotas();
    this._initializeUnicornQuotas();
  }

  private _handleNestedQuotasStreams() {
    this.selectedNestingGroup$ = this._multiQuotaNestingService
      .getSelectedNestingGroup()
      .pipe(
        tap((data) => {
          if (this._selectedNestingGroup.id !== data.id) {
            this.expandable?.reset();
            this.selecteds = [];
          }
          this._selectedNestingGroup = data;
          this.hasUnicornQuota = this.hasUnicornQuotaInSelectedGroup();
          this.remainingAllocation = this._remainingAllocation();
        }),
        shareReplay(1)
      );

    this.nestedQuotaRecords$ = this.selectedNestingGroup$.pipe(
      map((selected) => selected.quotas as NestedQuotaRecord[]),
      shareReplay(1)
    );

    this.nestedQuotaGroupIds$ = this.nestedQuotaRecords$.pipe(
      map((records) => this._getNestedQuotaGroupIds(records))
    );

    this.selectedQualifications$ = this.selectedNestingGroup$.pipe(
      map((selected) => selected.selectedQualifications)
    );
  }

  private _remainingAllocation(): number {
    return (
      this.surveyAllocation -
      new SurveyNestedQuotas(this._selectedNestingGroup.quotas).allocation()
    );
  }

  private _getNestedQuotaGroupIds(
    nestedQuotasRecord: NestedQuotaRecord[]
  ): string[] {
    return nestedQuotasRecord
      .filter((q) => !!q.individual_quotas?.length)
      .map((q) => q.buyer_quota_id)
      .filter((id): id is string => !!id);
  }

  public set selecteds(value: string[]) {
    // if all quotas are selected, filter out grouped quotas
    if (this._selectedNestingGroup?.quotas?.length === value.length) {
      this._selecteds = this._excludeGroupedQuotas(value);
    } else {
      this._selecteds = value;
    }
  }

  public get selecteds() {
    return this._selecteds;
  }

  public resetTable() {
    return this._openResetConfirmationModal()
      .pipe(
        filter((confirmed) => confirmed === 'ok'),
        map(() => {
          return {
            ...this._selectedNestingGroup,
            quotas: new NestedQuotaResetter(
              this._selectedNestingGroup,
              this.originalQuotas,
              this.surveyAllocation
            ).reset(),
            canResetTable: false,
          };
        })
      )
      .subscribe((selectedNestingGroup: EachNestingGroupData) => {
        this._multiQuotaNestingService.setNestingGroupData(
          selectedNestingGroup.id,
          selectedNestingGroup
        );
        this._selecteds = [];
        this._multiQuotaNestingService.indicateUnsavedChanges();
      });
  }

  public onCombineQuotas(): void {
    if (this._selecteds.length <= 1) {
      return this._toastr.warning(
        notifyMessage.errorMessage.MULTI_QUOTA_NESTING
          .AT_LEAST_TWO_SELECTIONS_REQUIRED
      );
    }

    if (this._selecteds.length === this._selectedNestingGroup.quotas.length) {
      return this._toastr.warning(
        notifyMessage.errorMessage.MULTI_QUOTA_NESTING
          .CANT_GROUP_ALL_CONDITIONS_IN_SINGLE_GROUP
      );
    }

    const { quotas: nestedQuotas, qualifications } = this._selectedNestingGroup;

    const { selectedQuotas, unselectedQuotas } =
      this._splitBySelection(nestedQuotas);

    const newGroupedQuota = new NestedQuotaGroupBuilder(
      selectedQuotas,
      qualifications,
      new SurveyNestedQuotas(nestedQuotas).titles(),
      this._selectedNestingGroup.id
    ).build();

    const sortedQuotas = new SurveyNestedQuotas([
      newGroupedQuota,
      ...unselectedQuotas,
    ]).sortByTitle();

    this._updateNestingGroupData({ quotas: sortedQuotas.get() });
    this._selecteds = []; // Clear selection
  }

  public OnUnGroupQuota(quota: NestedQuota, quotaIndex: number) {
    const nestedQuotas = [...this._selectedNestingGroup.quotas];
    const ungroupedQuotas = new NestedQuotaGroupSeparator(quota).ungroup();

    nestedQuotas.splice(quotaIndex, 1);
    nestedQuotas.push(...ungroupedQuotas);

    this._updateNestingGroupData({ quotas: nestedQuotas });
  }

  public deleteCondition() {
    this._deleteSelectedQuotasCommand$.next({
      ids: this._selecteds,
      type: 'multiple',
    });
  }

  public deleteQuota(buyer_quota_id: string) {
    this._deleteSelectedQuotasCommand$.next({
      ids: [buyer_quota_id],
      type: 'single',
    });
  }

  public onQuotaTitleUpdate(quotaId: string, title: string) {
    if (
      new SurveyNestedQuotas(this._selectedNestingGroup.quotas)
        .titles()
        .includes(title.trim())
    ) {
      this._toastr.warning('Quota titles should be unique.');
      return;
    }
    this._updateNestedQuotaInCurrentSelection(quotaId, {
      quota_title: title.trim(),
      isQuotaTitleEdit: false,
    });
  }

  public onQuotaCountUpdate(quota: NestedQuota, event: FocusEvent) {
    const parsedCount = parseInt((event.target as HTMLInputElement).value);
    if (this._checkIfQuotaCountMoreThanAllocation(quota, parsedCount)) {
      this._toastr.error(
        notifyMessage.errorMessage.MULTI_QUOTA_NESTING.INVALID_QUOTA_COMPLETE
      );
    }

    if (quota.individual_quotas?.length) {
      const allocatedIndividualQuotas =
        IndividualQuotaCountDistributor.forCountChange(
          quota,
          this.surveyAllocation
        ).distribute(parsedCount, this._quotaPercentage(parsedCount));
      this._updateNestedQuotaInCurrentSelection(
        quota.buyer_quota_id!,
        {
          required_count: parsedCount,
          number: parsedCount,
          maximum: parsedCount,
          minimum: parsedCount,
          percentage: this._quotaPercentage(parsedCount),
          individual_quotas: new IndividualQuotaAllocation(
            parsedCount,
            allocatedIndividualQuotas
          ).audit(),
          isAllocationEdit: false,
        },
        true
      );
      return;
    }

    this._updateNestedQuotaInCurrentSelection(
      quota.buyer_quota_id!,
      {
        required_count: parsedCount,
        number: parsedCount,
        maximum: parsedCount,
        minimum: parsedCount,
        percentage: this._quotaPercentage(parsedCount),
        isAllocationEdit: false,
      },
      true
    );
  }

  public onQuotaPercentageUpdate(quota: NestedQuota, event: FocusEvent) {
    const parsedPercentage = parseInt((event.target as HTMLInputElement).value);
    const count = this._quotaCount(parsedPercentage);
    if (this._checkIfQuotaCountMoreThanAllocation(quota, count)) {
      this._toastr.error(
        notifyMessage.errorMessage.MULTI_QUOTA_NESTING.INVALID_QUOTA_COMPLETE
      );
    }

    if (quota.individual_quotas?.length) {
      const allocatedIndividualQuotas =
        IndividualQuotaCountDistributor.forPercentageChange(
          quota,
          this.surveyAllocation
        ).distribute(count, parsedPercentage);
      this._updateNestedQuotaInCurrentSelection(
        quota.buyer_quota_id!,
        {
          required_count: count,
          number: count,
          maximum: count,
          minimum: count,
          percentage: parsedPercentage,
          individual_quotas: new IndividualQuotaAllocation(
            count,
            allocatedIndividualQuotas
          ).audit(),
          isPercentageEdit: false,
        },
        true
      );
      return;
    }

    this._updateNestedQuotaInCurrentSelection(
      quota.buyer_quota_id!,
      {
        required_count: count,
        number: count,
        maximum: count,
        minimum: count,
        percentage: parsedPercentage,
        isPercentageEdit: false,
      },
      true
    );
  }

  private _quotaPercentage(count: number): number {
    return Math.floor((100 * (count || 0)) / this.surveyAllocation);
  }

  private _quotaCount(percentage: number): number {
    return Math.round((this.surveyAllocation * Number(percentage)) / 100);
  }

  private _checkIfQuotaCountMoreThanAllocation(
    quota: NestedQuota,
    newCount: number
  ) {
    const restQuotas = this._selectedNestingGroup.quotas.filter(
      (q) => q.buyer_quota_id !== quota.buyer_quota_id
    );
    const totalCount =
      new SurveyNestedQuotas(restQuotas).allocation() + Number(newCount);
    return totalCount > this.surveyAllocation;
  }

  private _updateNestedQuotaInCurrentSelection(
    quotaId: string,
    update: Partial<NestedQuota>,
    canResetTable: boolean = false
  ): void {
    const updatedNestedQuotas = this._selectedNestingGroup.quotas.map(
      (nestedQuota) => {
        if (nestedQuota.buyer_quota_id === quotaId) {
          return {
            ...nestedQuota,
            ...update,
          };
        }
        return nestedQuota;
      }
    );
    const repairedNestedQuotas = new NestedQuotaPercentageMechanic(
      updatedNestedQuotas,
      this.surveyAllocation
    ).repair();
    const updatedSelectedNestingGroup = {
      ...this._selectedNestingGroup,
      canResetTable,
      quotas: repairedNestedQuotas,
    };
    this._updateNestingGroupData(updatedSelectedNestingGroup);
  }

  public onResetEditState(nestedQuota: NestedQuota, key: QuotaEditKey) {
    this._updateNestedQuotaInCurrentSelection(nestedQuota.buyer_quota_id!, {
      [key]: false,
    });
  }

  public onExpandRow(row: number): void {
    this.expandable.expand(row);
  }

  public onRetractRow(row: number): void {
    this.expandable.retract(row);
  }

  private _handleMultipleNestedQuotas() {
    this._deleteSelectedQuotasCommand$
      .pipe(
        filter(() => !!this._selectedNestingGroup.id),
        filter(({ ids }) => !!ids.length),
        filter(({ ids: selectedQuotaIds }) => {
          if (
            selectedQuotaIds.length === this._selectedNestingGroup.quotas.length
          ) {
            this._toastr.error(
              notifyMessage.errorMessage.MULTI_QUOTA_NESTING
                .KEEP_AT_LEAST_ONE_QUOTA
            );
            return false;
          }

          return true;
        }),
        switchMap(({ ids, type }) => {
          if (type === 'single') {
            return of({ ids });
          }

          return this._openDeleteConfirmationModal().pipe(
            filter((msg) => msg === 'ok'),
            map(() => ({ ids }))
          );
        })
      )
      .subscribe(({ ids: selectedQuotaIds }) => {
        const selectedQualIds =
          this._selectedNestingGroup.selectedQualifications.map((q) =>
            Number(q.qualification_code)
          );
        const filteredQuotas = this._selectedNestingGroup.quotas.filter(
          (quota) => !selectedQuotaIds.includes(quota.buyer_quota_id || '')
        );

        this.selecteds = this.selecteds.filter(
          (quotaId) => !selectedQuotaIds.includes(quotaId)
        );

        this._updateNestingGroupData({
          quotas: filteredQuotas,
          canResetTable: new NestedQuotasResetEvaluator(
            this.originalQuotas,
            selectedQualIds,
            filteredQuotas
          ).evaluate(),
        });
      });
  }

  private _updateNestingGroupData(
    selectedNestingGroup: Partial<EachNestingGroupData>
  ) {
    this._multiQuotaNestingService.setNestingGroupData(
      this._selectedNestingGroup.id,
      {
        ...this._selectedNestingGroup,
        ...selectedNestingGroup,
      }
    );

    this._multiQuotaNestingService.indicateUnsavedChanges();
  }

  private _openResetConfirmationModal() {
    return this._openConfirmationModal(
      Constants.CONFIRM_MODAL_TEXT.MULTI_QUOTA_NESTING_MODAL
        .RESET_CONFIRMATION_MODAL
    );
  }

  private _openDeleteConfirmationModal() {
    return this._openConfirmationModal(
      Constants.CONFIRM_MODAL_TEXT.MULTI_QUOTA_NESTING_MODAL.DELETE_CONFIRMATION
    );
  }

  private _openConfirmationModal(message: string) {
    const modalRef = this._modal.open(ConfirmationModalComponent, {
      data: {
        text: message,
        size: 'md',
        confirmButtonLabel: 'Confirm',
      },
    });
    return modalRef.onClose$;
  }

  private _splitBySelection(quotas: NestedQuota[] = []): {
    selectedQuotas: NestedQuota[];
    unselectedQuotas: NestedQuota[];
  } {
    const selectedQuotas: NestedQuota[] = [];
    const unselectedQuotas: NestedQuota[] = [];

    for (const quota of quotas) {
      if (
        quota.buyer_quota_id &&
        this._selecteds.includes(quota.buyer_quota_id)
      ) {
        selectedQuotas.push(quota);
      } else {
        unselectedQuotas.push(quota);
      }
    }

    return { selectedQuotas, unselectedQuotas };
  }

  /**
   * Filter and return only quotas that are not grouped.
   * If `quotas` is an array of ids, return the ids of the quotas that are not grouped.
   * If `quotas` is an array of NestedQuota objects, return the NestedQuota objects that are not grouped.
   * @param quotas an array of NestedQuota objects or an array of ids.
   * @returns an array of NestedQuota objects or an array of ids.
   */
  private _excludeGroupedQuotas<T extends string | NestedQuota>(
    quotas: T[] = []
  ): T[] {
    if (!quotas.length || !this._selectedNestingGroup?.quotas?.length) {
      return [];
    }

    if (isNestedQuota(quotas[0])) {
      return (quotas as NestedQuota[]).filter(
        ({ individual_quotas = [] }) => !individual_quotas.length
      ) as T[];
    }

    const groupedQuotaIds = this._selectedNestingGroup.quotas
      .filter(({ individual_quotas = [] }) => individual_quotas.length)
      .map(({ buyer_quota_id }) => buyer_quota_id);

    return (quotas as string[]).filter(
      (id) => !groupedQuotaIds.includes(id)
    ) as T[];
  }

  private _isCombineQuotasEnabled() {
    if (this._authService.userType === 'operator' && this.buyerCompanyId) {
      return new BuyerConfigSettingsVerifier(
        this._authService.allBuyerConfigs
      ).verifyPermission('enableNestingQuotaGrouping', this.buyerCompanyId);
    }

    return !!this._authService.buyerConfig?.enableNestingQuotaGrouping;
  }

  private _initializeUnicornQuotas(): void {
    this._quotaPayloadService.getSurveyUnicorns().subscribe((response) => {
      this.surveyUnicorns = new SurveyUnicorns(response || []);
    });
  }

  public hasUnicornQuotaInSelectedGroup() {
    return this._selectedNestingGroup.quotas.some((quota) =>
      this.surveyUnicorns.hasQuota(quota.buyer_quota_id!)
    );
  }

  public getRowHighlightColor = (nestedQuota: NestedQuotaRecord): string => {
    if (this.surveyUnicorns.hasQuota(nestedQuota.buyer_quota_id!)) {
      return '#fce8ea';
    }
    return '';
  };
}
